// src/components/module/ventas/BuscadorClientes.tsx
import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Fade,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { DarkModeContext } from '../../../context/darkModeContext';

interface Cliente {
  Id: number;
  NombreCompleto: string;
  NumeroDocumento: string;
  Telefono?: string;
  Direccion?: string;
  Correo?: string;
}

interface BuscadorClientesProps {
  onClienteSeleccionado: (cliente: { id: number; nombre: string }) => void;
  disabled?: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4020';

// Tema profesional
export const BuscadorClientes: React.FC<BuscadorClientesProps> = ({
  onClienteSeleccionado,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<Cliente | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { darkMode } = useContext(DarkModeContext);

  useEffect(() => {
    const buscarClientes = async () => {
      if (searchTerm.length < 2) {
        setClientes([]);
        setShowResults(false);
        return;
      }

      setLoading(true);
      try {
        const { data } = await axios.post(`${API_URL}/administracion/clientes/buscar`, {
          termino: searchTerm  // ← Agregar esto
        });
      
        setClientes(data.slice(0, 8)); // Limitar a 8 resultados
        setShowResults(true);
      } catch (error) {
        console.error('Error buscando clientes:', error);
        setClientes([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      buscarClientes();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleSeleccionarCliente = (cliente: Cliente) => {
    setClienteSeleccionado(cliente);
    setSearchTerm(cliente.NombreCompleto);
    setShowResults(false);
    onClienteSeleccionado({
      id: cliente.Id,
      nombre: cliente.NombreCompleto
    });
  };

  const handleLimpiar = () => {
    setClienteSeleccionado(null);
    setSearchTerm('');
    setClientes([]);
    setShowResults(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (disabled || clienteSeleccionado) return;
    if (containerRef.current?.contains(e.relatedTarget as Node)) return;
    setTimeout(() => { if (!inputRef.current?.disabled) inputRef.current?.focus(); }, 100);
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
        {/* Campo de búsqueda */}
        <TextField
          fullWidth
          placeholder="Buscar cliente por nombre o identificación..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={disabled || !!clienteSeleccionado}
          autoFocus={!disabled && !clienteSeleccionado}
          inputRef={inputRef}
          onBlur={handleBlur}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading ? (
                  <CircularProgress size={20} sx={{ color: 'primary.main' }} />
                ) : (
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                )}
              </InputAdornment>
            ),
            endAdornment: clienteSeleccionado && (
              <InputAdornment position="end">
                <Tooltip title="Cambiar cliente" arrow>
                  <IconButton
                    size="small"
                    onClick={handleLimpiar}
                    sx={{
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'rgba(239, 68, 68, 0.08)',
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'background.paper',
              '&:hover:not(.Mui-disabled) fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: 2,
              },
              '&.Mui-disabled': {
                bgcolor: darkMode ? 'rgba(148, 163, 184, 0.05)' : 'rgba(100, 116, 139, 0.05)',
              }
            },
          }}
        />

        {/* Cliente seleccionado */}
        {clienteSeleccionado && (
          <Fade in timeout={300}>
            <Paper
              elevation={2}
              sx={{
                mt: 2,
                p: 2.5,
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'success.main',
                background: darkMode
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.02) 100%)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: 'success.main',
                    width: 56,
                    height: 56,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 32 }} />
                </Avatar>

                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {clienteSeleccionado.NombreCompleto}
                    </Typography>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Seleccionado"
                      size="small"
                      color="success"
                      sx={{
                        fontWeight: 700,
                        height: 24,
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BadgeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        <strong>Identificación:</strong> {clienteSeleccionado.NumeroDocumento}
                      </Typography>
                    </Box>

                    {clienteSeleccionado.Telefono && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          <strong>Teléfono:</strong> {clienteSeleccionado.Telefono}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Fade>
        )}

        {/* Resultados de búsqueda */}
        {showResults && clientes.length > 0 && !clienteSeleccionado && (
          <Fade in timeout={200}>
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                mt: 1,
                maxHeight: 400,
                overflow: 'auto',
                zIndex: 1000,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <List sx={{ p: 0 }}>
                {clientes.map((cliente, index) => (
                  <React.Fragment key={cliente.Id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleSeleccionarCliente(cliente)}
                        sx={{
                          py: 2,
                          px: 2.5,
                          '&:hover': {
                            bgcolor: darkMode ? 'rgba(8, 145, 178, 0.12)' : 'rgba(8, 145, 178, 0.08)',
                          },
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: 'primary.main',
                            width: 40,
                            height: 40,
                            mr: 2,
                          }}
                        >
                          <PersonIcon />
                        </Avatar>
                        <ListItemText
                          primary={
                            <Typography variant="body1" fontWeight={600}>
                              {cliente.NombreCompleto}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <BadgeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {cliente.NumeroDocumento}
                                </Typography>
                              </Box>
                              {cliente.Telefono && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {cliente.Telefono}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < clientes.length - 1 && (
                      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mx: 2 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Fade>
        )}

        {/* Sin resultados */}
        {showResults && clientes.length === 0 && !loading && searchTerm.length >= 2 && (
          <Fade in timeout={200}>
            <Paper
              elevation={2}
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                mt: 1,
                p: 3,
                zIndex: 1000,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                textAlign: 'center',
              }}
            >
              <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                No se encontraron clientes
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Intenta con otro término de búsqueda
              </Typography>
            </Paper>
          </Fade>
        )}

        {/* Ayuda */}
        {!clienteSeleccionado && searchTerm.length === 0 && (
          <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: darkMode ? 'rgba(8, 145, 178, 0.08)' : 'rgba(8, 145, 178, 0.04)' }}>
            <Typography variant="caption" color="text.secondary">
              💡 <strong>Tip:</strong> Escribe al menos 2 caracteres para buscar clientes por nombre o identificación
            </Typography>
          </Box>
        )}
      </Box>
  );
};